import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { createEvent, getEventDetails, updateEvent } from '../api/services';
import {
  getApiErrorMessage,
  slugToLabel,
  toDateInputValue,
  toIsoDateString,
} from '../utils/admin';

const defaultFormData = {
  title: '',
  description: '',
  date: '',
  startTime: '',
  endTime: '',
  venue: {
    name: '',
    city: '',
    googleMapsLink: '',
    coordinates: {
      latitude: '',
      longitude: '',
    },
  },
  maxAttendees: 20,
  price: 0,
  currency: 'INR',
  earlyBirdPrice: '',
  earlyBirdDeadline: '',
  coverImage: '',
  galleryImages: [''],
  category: 'coffee',
  customTags: [''],
  whatToExpect: [''],
  highlights: [''],
  requirements: [''],
  allowWaitlist: true,
  autoPromoteFromWaitlist: true,
  registrationDeadline: '',
  status: 'draft',
};

const categoryOptions = [
  { value: 'coffee', label: 'Coffee' },
  { value: 'meal', label: 'Meal' },
  { value: 'networking', label: 'Networking' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'sports', label: 'Sports' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'social', label: 'Social' },
  { value: 'professional', label: 'Professional' },
  { value: 'other', label: 'Other' },
];

const currencyOptions = ['INR', 'USD', 'EUR', 'GBP'];
const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

const EventForm = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(eventId);

  const [formData, setFormData] = useState(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditing);
  const [loadedEventType, setLoadedEventType] = useState('official');

  const fetchEventData = useCallback(async () => {
    if (!eventId) {
      return;
    }

    setFetchingData(true);

    try {
      const response = await getEventDetails(eventId);
      const event = response.data;

      setLoadedEventType(event.eventType || 'official');
      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: toDateInputValue(event.date),
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        venue: {
          name: event.venue?.name || '',
          city: event.venue?.city || '',
          googleMapsLink: event.venue?.googleMapsLink || '',
          coordinates: {
            latitude: event.venue?.coordinates?.latitude ?? '',
            longitude: event.venue?.coordinates?.longitude ?? '',
          },
        },
        maxAttendees: event.maxAttendees || 20,
        price: event.price || 0,
        currency: event.currency || 'INR',
        earlyBirdPrice: event.earlyBirdPrice ?? '',
        earlyBirdDeadline: toDateInputValue(event.earlyBirdDeadline),
        coverImage: event.coverImage || '',
        galleryImages: event.galleryImages?.length ? event.galleryImages : [''],
        category: event.category || 'coffee',
        customTags: event.customTags?.length ? event.customTags : [''],
        whatToExpect: event.whatToExpect?.length ? event.whatToExpect : [''],
        highlights: event.highlights?.length ? event.highlights : [''],
        requirements: event.requirements?.length ? event.requirements : [''],
        allowWaitlist: event.allowWaitlist ?? true,
        autoPromoteFromWaitlist: event.autoPromoteFromWaitlist ?? true,
        registrationDeadline: toDateInputValue(event.registrationDeadline),
        status: event.status === 'published' ? 'published' : 'draft',
      });
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load event data'));
    } finally {
      setFetchingData(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (isEditing) {
      fetchEventData();
    }
  }, [fetchEventData, isEditing]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name.startsWith('venue.')) {
      const field = name.split('.')[1];
      if (field === 'latitude' || field === 'longitude') {
        setFormData((previous) => ({
          ...previous,
          venue: {
            ...previous.venue,
            coordinates: {
              ...previous.venue.coordinates,
              [field]: value,
            },
          },
        }));
        return;
      }

      setFormData((previous) => ({
        ...previous,
        venue: {
          ...previous.venue,
          [field]: value,
        },
      }));
      return;
    }

    setFormData((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: previous[field].map((item, itemIndex) =>
        itemIndex === index ? value : item
      ),
    }));
  };

  const addArrayItem = (field) => {
    setFormData((previous) => ({
      ...previous,
      [field]: [...previous[field], ''],
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((previous) => ({
      ...previous,
      [field]: previous[field].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const validateForm = () => {
    if (formData.endTime <= formData.startTime) {
      toast.error('End time must be after start time.');
      return false;
    }

    const hasLatitude = String(formData.venue.coordinates.latitude).trim() !== '';
    const hasLongitude = String(formData.venue.coordinates.longitude).trim() !== '';

    if (hasLatitude !== hasLongitude) {
      toast.error('Provide both latitude and longitude, or leave both blank.');
      return false;
    }

    if (formData.earlyBirdPrice !== '' && !formData.earlyBirdDeadline) {
      toast.error('Early bird deadline is required when early bird price is set.');
      return false;
    }

    if (
      formData.earlyBirdPrice !== '' &&
      Number(formData.price) > 0 &&
      Number(formData.earlyBirdPrice) >= Number(formData.price)
    ) {
      toast.error('Early bird price must be less than the regular price.');
      return false;
    }

    if (formData.registrationDeadline && formData.registrationDeadline >= formData.date) {
      toast.error('Registration deadline must be before the event date.');
      return false;
    }

    if (formData.earlyBirdDeadline && formData.earlyBirdDeadline >= formData.date) {
      toast.error('Early bird deadline must be before the event date.');
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const cleanedGalleryImages = formData.galleryImages.filter((item) => item.trim());
    const cleanedCustomTags = formData.customTags.filter((item) => item.trim());
    const cleanedExpectations = formData.whatToExpect.filter((item) => item.trim());
    const cleanedHighlights = formData.highlights.filter((item) => item.trim());
    const cleanedRequirements = formData.requirements.filter((item) => item.trim());

    const payload = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      date: toIsoDateString(formData.date),
      startTime: formData.startTime,
      endTime: formData.endTime,
      venue: {
        name: formData.venue.name.trim(),
        city: formData.venue.city.trim(),
      },
      maxAttendees: parseInt(formData.maxAttendees, 10),
      price: Number(formData.price) || 0,
      currency: (formData.currency || 'INR').trim().toUpperCase(),
      coverImage: formData.coverImage.trim(),
      category: formData.category,
      allowWaitlist: formData.allowWaitlist,
      autoPromoteFromWaitlist: formData.autoPromoteFromWaitlist,
      status: formData.status,
    };

    if (formData.venue.googleMapsLink.trim()) {
      payload.venue.googleMapsLink = formData.venue.googleMapsLink.trim();
    }

    if (
      String(formData.venue.coordinates.latitude).trim() &&
      String(formData.venue.coordinates.longitude).trim()
    ) {
      payload.venue.coordinates = {
        latitude: parseFloat(formData.venue.coordinates.latitude),
        longitude: parseFloat(formData.venue.coordinates.longitude),
      };
    }

    if (cleanedGalleryImages.length) {
      payload.galleryImages = cleanedGalleryImages;
    }

    if (cleanedCustomTags.length) {
      payload.customTags = cleanedCustomTags;
    }

    if (cleanedExpectations.length) {
      payload.whatToExpect = cleanedExpectations;
    }

    if (cleanedHighlights.length) {
      payload.highlights = cleanedHighlights;
    }

    if (cleanedRequirements.length) {
      payload.requirements = cleanedRequirements;
    }

    if (formData.earlyBirdPrice !== '') {
      payload.earlyBirdPrice = Number(formData.earlyBirdPrice);
      payload.earlyBirdDeadline = toIsoDateString(formData.earlyBirdDeadline);
    }

    if (formData.registrationDeadline) {
      payload.registrationDeadline = toIsoDateString(formData.registrationDeadline);
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = buildPayload();

      if (isEditing) {
        const response = await updateEvent(eventId, payload);
        toast.success(response.message || 'Event updated successfully');
      } else {
        const response = await createEvent(payload);
        toast.success(response.message || 'Event created successfully');
      }

      navigate('/events');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save event'));

      const validationDetails = error?.response?.data?.details;
      if (validationDetails && typeof validationDetails === 'object') {
        Object.entries(validationDetails).forEach(([field, message]) => {
          toast.error(`${field}: ${message}`);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex justify-center p-12">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <button
        onClick={() => navigate('/events')}
        className="inline-flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} className="mr-1" /> Back to Events
      </button>

      <div className="rounded-xl bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 pb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Event' : 'Create Official Event'}
          </h1>
          <p className="text-sm text-gray-500">
            This form now follows the current admin event API contract.
          </p>
          {!isEditing && (
            <div className="inline-flex items-start rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <Info size={18} className="mr-2 mt-0.5 flex-shrink-0" />
              The backend auto-publishes official events on creation, even if you submit them as draft.
            </div>
          )}
          {isEditing && loadedEventType !== 'official' && (
            <div className="inline-flex items-start rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <Info size={18} className="mr-2 mt-0.5 flex-shrink-0" />
              This is a {slugToLabel(loadedEventType)} event. Only the shared event fields are editable here.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-8 pt-6">
          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Basic Information</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Coffee Morning"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  maxLength={5000}
                  rows={6}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="A casual coffee meetup for networking and conversation."
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Date & Time</h2>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  End Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Registration Deadline
                </label>
                <input
                  type="date"
                  name="registrationDeadline"
                  value={formData.registrationDeadline}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Venue</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Venue Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="venue.name"
                  value={formData.venue.name}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Blue Tokai Coffee"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="venue.city"
                  value={formData.venue.city}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Mumbai"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Google Maps Link</label>
                <input
                  type="url"
                  name="venue.googleMapsLink"
                  value={formData.venue.googleMapsLink}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://maps.google.com/..."
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Latitude</label>
                <input
                  type="number"
                  step="any"
                  name="venue.latitude"
                  value={formData.venue.coordinates.latitude}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="19.07"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Longitude</label>
                <input
                  type="number"
                  step="any"
                  name="venue.longitude"
                  value={formData.venue.coordinates.longitude}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="72.87"
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Capacity & Pricing</h2>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Max Attendees <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="maxAttendees"
                  value={formData.maxAttendees}
                  onChange={handleChange}
                  min={1}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min={0}
                  step="0.01"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {currencyOptions.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Early Bird Price</label>
                <input
                  type="number"
                  name="earlyBirdPrice"
                  value={formData.earlyBirdPrice}
                  onChange={handleChange}
                  min={0}
                  step="0.01"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Early Bird Deadline</label>
                <input
                  type="date"
                  name="earlyBirdDeadline"
                  value={formData.earlyBirdDeadline}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Images</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Cover Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="coverImage"
                  value={formData.coverImage}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/cover.jpg"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Gallery Images</label>
                {formData.galleryImages.map((url, index) => (
                  <div key={`gallery-${index}`} className="mb-2 flex gap-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(event) => handleArrayChange('galleryImages', index, event.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/photo.jpg"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('galleryImages', index)}
                      className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('galleryImages')}
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  <Plus size={16} className="mr-1" /> Add Image
                </button>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Event Details</h2>
            <div className="space-y-6">
              {[
                { field: 'whatToExpect', label: 'What to Expect', placeholder: 'Great coffee' },
                { field: 'highlights', label: 'Highlights', placeholder: 'Expert barista session' },
                { field: 'requirements', label: 'Requirements', placeholder: 'Bring your own laptop' },
                { field: 'customTags', label: 'Custom Tags', placeholder: 'morning' },
              ].map((section) => (
                <div key={section.field}>
                  <label className="mb-2 block text-sm font-medium text-gray-700">{section.label}</label>
                  {formData[section.field].map((value, index) => (
                    <div key={`${section.field}-${index}`} className="mb-2 flex gap-2">
                      <input
                        type="text"
                        value={value}
                        onChange={(event) => handleArrayChange(section.field, index, event.target.value)}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={section.placeholder}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem(section.field, index)}
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem(section.field)}
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Plus size={16} className="mr-1" /> Add Item
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-900">Settings</h2>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="allowWaitlist"
                  checked={formData.allowWaitlist}
                  onChange={handleChange}
                  className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Allow waitlist when event is full</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="autoPromoteFromWaitlist"
                  checked={formData.autoPromoteFromWaitlist}
                  onChange={handleChange}
                  className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Auto-promote from waitlist</span>
              </label>
            </div>
          </section>

          <div className="flex justify-end gap-3 border-t border-gray-100 pt-6">
            <button
              type="button"
              onClick={() => navigate('/events')}
              className="rounded-lg px-6 py-2 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Saving...' : isEditing ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;
